/** Błąd zwrócony przez API w ustandaryzowanym kształcie `{ error: { code, message } }`. */
export class BladApi extends Error {
  constructor(
    readonly status: number,
    readonly kod: string,
    komunikat: string,
  ) {
    super(komunikat)
    this.name = 'BladApi'
  }
}

/**
 * Token dostępowy żyje wyłącznie w pamięci karty. Trzymanie go w
 * `localStorage` wystawiłoby go na każdy zastrzyk skryptu; sesję między
 * odświeżeniami odtwarzamy z ciasteczka odświeżającego (httpOnly).
 */
let token: string | null = null
let odswiezanie: Promise<boolean> | null = null
/** Identyfikator zalogowanego — potrzebny, gdy rekord powstaje offline. */
let idUzytkownika: string | null = null

export function ustawToken(nowy: string | null): void {
  token = nowy
}

export function ustawIdUzytkownika(id: string | null): void {
  idUzytkownika = id
}

export function biezacyIdUzytkownika(): string {
  return idUzytkownika ?? 'nieznany'
}

export function maToken(): boolean {
  return token !== null
}

interface Opcje {
  metoda?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  operationId?: string
  /** Wyłącza jednokrotną próbę odświeżenia tokenu — używane przez samo odświeżanie. */
  bezOdswiezania?: boolean
}

async function wyslij(sciezka: string, opcje: Opcje): Promise<Response> {
  const naglowki: Record<string, string> = {}
  if (opcje.body !== undefined) naglowki['content-type'] = 'application/json'
  if (token !== null) naglowki.authorization = `Bearer ${token}`
  if (opcje.operationId !== undefined) naglowki['x-operation-id'] = opcje.operationId

  return fetch(sciezka, {
    method: opcje.metoda ?? 'GET',
    headers: naglowki,
    body: opcje.body === undefined ? undefined : JSON.stringify(opcje.body),
    credentials: 'include',
  })
}

/** Wymienia ciasteczko odświeżające na nowy token. Równoległe wywołania dzielą jedną próbę. */
export function odswiezToken(): Promise<boolean> {
  odswiezanie ??= (async () => {
    try {
      const odpowiedz = await wyslij('/api/auth/refresh', {
        metoda: 'POST',
        bezOdswiezania: true,
      })
      if (!odpowiedz.ok) {
        token = null
        return false
      }
      const dane = (await odpowiedz.json()) as { accessToken: string }
      token = dane.accessToken
      return true
    } catch {
      return false
    } finally {
      odswiezanie = null
    }
  })()
  return odswiezanie
}

async function bladZOdpowiedzi(odpowiedz: Response): Promise<BladApi> {
  try {
    const dane = (await odpowiedz.json()) as { error?: { code?: string; message?: string } }
    return new BladApi(
      odpowiedz.status,
      dane.error?.code ?? 'blad',
      dane.error?.message ?? 'Coś poszło nie tak',
    )
  } catch {
    return new BladApi(odpowiedz.status, 'blad', 'Serwer nie odpowiedział poprawnie')
  }
}

async function zadanie<T>(sciezka: string, opcje: Opcje = {}): Promise<T> {
  let odpowiedz = await wyslij(sciezka, opcje)

  if (odpowiedz.status === 401 && opcje.bezOdswiezania !== true) {
    if (await odswiezToken()) odpowiedz = await wyslij(sciezka, opcje)
  }

  if (!odpowiedz.ok) throw await bladZOdpowiedzi(odpowiedz)
  if (odpowiedz.status === 204) return undefined as T

  const typ = odpowiedz.headers.get('content-type') ?? ''
  if (!typ.includes('application/json')) return (await odpowiedz.text()) as T
  return (await odpowiedz.json()) as T
}

export const api = {
  get: <T>(sciezka: string) => zadanie<T>(sciezka),
  post: <T>(sciezka: string, body?: unknown, operationId?: string) =>
    zadanie<T>(sciezka, { metoda: 'POST', body, operationId }),
  patch: <T>(sciezka: string, body: unknown, operationId?: string) =>
    zadanie<T>(sciezka, { metoda: 'PATCH', body, operationId }),
  del: <T>(sciezka: string, operationId?: string) =>
    zadanie<T>(sciezka, { metoda: 'DELETE', operationId }),
}

/** Loguje i zapamiętuje token; ciasteczko odświeżające ustawia serwer. */
export async function zalogujZadanie(
  email: string,
  haslo: string,
): Promise<{ accessToken: string; user: { id: string; email: string; name: string } }> {
  const dane = await zadanie<{
    accessToken: string
    user: { id: string; email: string; name: string }
  }>('/api/auth/login', {
    metoda: 'POST',
    body: { email, password: haslo },
    bezOdswiezania: true,
  })
  token = dane.accessToken
  return dane
}

export async function wylogujZadanie(): Promise<void> {
  try {
    await zadanie('/api/auth/logout', { metoda: 'POST' })
  } finally {
    token = null
  }
}
